/**
 * 产品详情配置文件（国际化版本）
 * 使用翻译键而不是硬编码的文本
 * 
 * 产品类型说明：
 * - web: Web AI车队（共享车队服务）
 * - app: APP账号（独享账号）
 * - monkey: Monkey AI（AI Monkey独家服务）
 * - diamond: 钻石（高级会员服务）
 */

import i18n from '../i18n';

// 辅助函数：获取翻译文本
const t = (key, options) => i18n.t(key, options);

// 通用的特性配置
const commonFeatures = {
  originalWebsite: { 
    icon: '🚀', 
    titleKey: 'productDetail.common.features.originalWebsite.title',
    descKey: 'productDetail.common.features.originalWebsite.desc'
  },
  privateConversation: { 
    icon: '🧠', 
    titleKey: 'productDetail.common.features.privateConversation.title',
    descKey: 'productDetail.common.features.privateConversation.desc'
  },
  neverBanned: { 
    icon: '🔒', 
    titleKey: 'productDetail.common.features.neverBanned.title',
    descKey: 'productDetail.common.features.neverBanned.desc'
  },
  webAndApp: { 
    icon: '📱', 
    titleKey: 'productDetail.common.features.webAndApp.title',
    descKey: 'productDetail.common.features.webAndApp.desc'
  },
  changePassword: { 
    icon: '🎤', 
    titleKey: 'productDetail.common.features.changePassword.title',
    descKey: 'productDetail.common.features.changePassword.desc'
  },
  banProtection: { 
    icon: '📸', 
    titleKey: 'productDetail.common.features.banProtection.title',
    descKey: 'productDetail.common.features.banProtection.desc'
  }
};

// 通用的FAQ配置
const commonFAQ = {
  freeTrial: {
    questionKey: 'productDetail.common.faq.freeTrial.q',
    answerKey: 'productDetail.common.faq.freeTrial.a'
  },
  refund: {
    questionKey: 'productDetail.common.faq.refund.q',
    answerKey: 'productDetail.common.faq.refund.a'
  },
  refundApp: {
    questionKey: 'productDetail.common.faq.refundApp.q',
    answerKey: 'productDetail.common.faq.refundApp.a'
  },
  freeTrialApp: {
    questionKey: 'productDetail.common.faq.freeTrialApp.q',
    answerKey: 'productDetail.common.faq.freeTrialApp.a'
  },
  changePassword: {
    questionKey: 'productDetail.common.faq.changePassword.q',
    answerKey: 'productDetail.common.faq.changePassword.a'
  },
  support: {
    questionKey: 'productDetail.common.faq.support.q',
    answerKey: 'productDetail.common.faq.support.a'
  },
  conversationPrivacy: {
    questionKey: 'productDetail.common.faq.conversationPrivacy.q',
    answerKey: 'productDetail.common.faq.conversationPrivacy.a'
  },
  exclusiveAccount: {
    questionKey: 'productDetail.common.faq.exclusiveAccount.q',
    answerKey: 'productDetail.common.faq.exclusiveAccount.a'
  },
  wantExclusive: {
    questionKey: 'productDetail.common.faq.wantExclusive.q',
    answerKey: 'productDetail.common.faq.wantExclusive.a'
  }
};

export const productDetailConfigI18n = {
  // ChatGPT Plus 产品配置
  gpt: {
    id: 'gpt',
    nameKey: 'shop.categories.gpt',
    type: 'web',
    descriptionKey: 'productDetail.gpt.description',
    highlightKeys: [
      'productDetail.common.highlights.originalWebsite',
      'productDetail.common.highlights.freeTrial',
      'productDetail.gpt.highlights.o3Model',
      'productDetail.gpt.highlights.unlimited4o',
      'productDetail.common.highlights.voiceChat',
      'productDetail.common.highlights.internetSearch',
      'productDetail.common.highlights.fileRecognition'
    ],
    features: [
      commonFeatures.originalWebsite,
      commonFeatures.privateConversation,
      { 
        icon: '🔌', 
        titleKey: 'productDetail.gpt.features.unlimited4o.title',
        descKey: 'productDetail.gpt.features.unlimited4o.desc'
      },
      commonFeatures.neverBanned
    ],
    faqKeys: [
      commonFAQ.freeTrial,
      commonFAQ.refund,
      {
        questionKey: 'productDetail.gpt.faq.whatIsService.q',
        answerKey: 'productDetail.gpt.faq.whatIsService.a'
      },
      {
        questionKey: 'productDetail.gpt.faq.usageLimit.q',
        answerKey: 'productDetail.gpt.faq.usageLimit.a'
      },
      commonFAQ.conversationPrivacy,
      commonFAQ.exclusiveAccount,
      commonFAQ.wantExclusive,
      {
        questionKey: 'productDetail.gpt.faq.supportedFeatures.q',
        answerKey: 'productDetail.gpt.faq.supportedFeatures.a'
      },
      {
        questionKey: 'productDetail.gpt.faq.difference.q',
        answerKey: 'productDetail.gpt.faq.difference.a'
      },
      commonFAQ.support
    ],
    versions: [
      {
        label: '1 month',
        month: 1,
        price: 6.9,
        originalPrice: 20,
        stock: 99,
        productId: '228',
        key: 'monthly',
        durationKey: 'productDetail.common.duration.month'
      },
      {
        label: '3 months',
        month: 3,
        price: 17.7,
        originalPrice: 60,
        stock: 99,
        productId: '229',
        key: 'quarterly',
        durationKey: 'productDetail.common.duration.month'
      },
      {
        label: '12 months',
        month: 12,
        price: 58.8,
        originalPrice: 240,
        stock: 99,
        productId: '230',
        key: 'yearly',
        durationKey: 'productDetail.common.duration.month'
      }
    ],
    specs: [
      { labelKey: 'productDetail.common.specs.environment', valueKeys: ['productDetail.common.specs.values.webBrowser'] },
      { labelKey: 'productDetail.common.specs.productType', valueKeys: ['productDetail.common.specs.values.sharedFleet'] },
      { labelKey: 'productDetail.common.specs.accountType', valueKeys: ['productDetail.common.specs.values.sharedFleetAccess'] },
      { labelKey: 'productDetail.common.specs.dataPrivacy', valueKeys: ['productDetail.common.specs.values.privateConversation'] },
      { labelKey: 'productDetail.common.specs.warranty', valueKeys: ['productDetail.common.specs.values.warrantyDuration'] },
      { labelKey: 'productDetail.common.specs.packageIncludes', values: ['GPT+Sora'] },
      { labelKey: 'productDetail.common.specs.loginEnvironment', valueKeys: ['productDetail.common.specs.values.browserAiMonkey'] },
      { labelKey: 'productDetail.common.specs.officialPrice', values: ['20$/月'] },
      { labelKey: 'productDetail.common.specs.rateLimit', valueKeys: ['productDetail.gpt.specs.rateLimit'] }
    ]
  },

  // Claude Pro 产品配置
  claude: {
    id: 'claude',
    nameKey: 'shop.categories.claude',
    type: 'web',
    descriptionKey: 'productDetail.claude.description',
    highlightKeys: [
      'productDetail.common.highlights.originalWebsite',
      'productDetail.common.highlights.freeTrial',
      'productDetail.claude.highlights.claude4Model'
    ],
    features: [
      commonFeatures.originalWebsite,
      commonFeatures.privateConversation,
      { 
        icon: '🔌', 
        titleKey: 'productDetail.claude.features.modelLimit.title',
        descKey: 'productDetail.claude.features.modelLimit.desc'
      },
      commonFeatures.neverBanned
    ],
    faqKeys: [
      commonFAQ.freeTrial,
      commonFAQ.refund,
      {
        questionKey: 'productDetail.claude.faq.whatIsService.q',
        answerKey: 'productDetail.claude.faq.whatIsService.a'
      },
      {
        questionKey: 'productDetail.claude.faq.usageLimit.q',
        answerKey: 'productDetail.claude.faq.usageLimit.a'
      },
      commonFAQ.conversationPrivacy,
      commonFAQ.exclusiveAccount,
      commonFAQ.wantExclusive,
      {
        questionKey: 'productDetail.claude.faq.supportedFeatures.q',
        answerKey: 'productDetail.claude.faq.supportedFeatures.a'
      },
      {
        questionKey: 'productDetail.claude.faq.difference.q',
        answerKey: 'productDetail.claude.faq.difference.a'
      },
      commonFAQ.support
    ],
    versions: [
      { 
        label: '1 month',
        month: 1,
        price: 8.9,
        originalPrice: 20,
        stock: 99,
        productId: '231',
        key: 'monthly',
        durationKey: 'productDetail.common.duration.month'
      },
      {
        label: '3 months',
        month: 3,
        price: 23.7,
        originalPrice: 60,
        stock: 99,
        productId: '232',
        key: 'quarterly',
        durationKey: 'productDetail.common.duration.month'
      },
      {
        label: '12 months',
        month: 12,
        price: 82.8,
        originalPrice: 240,
        stock: 99,
        productId: '233',
        key: 'yearly',
        durationKey: 'productDetail.common.duration.month'
      }
    ],
    specs: [
      { labelKey: 'productDetail.common.specs.environment', valueKeys: ['productDetail.common.specs.values.webBrowser'] },
      { labelKey: 'productDetail.common.specs.productType', valueKeys: ['productDetail.common.specs.values.sharedFleet'] },
      { labelKey: 'productDetail.common.specs.accountType', valueKeys: ['productDetail.common.specs.values.sharedFleetAccess'] },
      { labelKey: 'productDetail.common.specs.dataPrivacy', valueKeys: ['productDetail.common.specs.values.privateConversation'] },
      { labelKey: 'productDetail.common.specs.warranty', valueKeys: ['productDetail.common.specs.values.warrantyDuration'] },
      { labelKey: 'productDetail.common.specs.packageIncludes', values: ['GPT+Sora+Claude+Grok'] },
      { labelKey: 'productDetail.common.specs.loginEnvironment', valueKeys: ['productDetail.common.specs.values.browserAiMonkey'] },
      { labelKey: 'productDetail.common.specs.officialPrice', values: ['20$/月'] },
      { labelKey: 'productDetail.common.specs.rateLimit', valueKeys: ['productDetail.claude.specs.rateLimit'] }
    ]
  },

  // Sora Plus 产品配置
  sora: {
    id: 'sora',
    nameKey: 'shop.categories.sora',
    type: 'web',
    descriptionKey: 'productDetail.sora.description',
    highlightKeys: [
      'productDetail.common.highlights.originalWebsite',
      'productDetail.common.highlights.freeTrial',
      'productDetail.common.highlights.imageGeneration',
      'productDetail.common.highlights.videoGeneration'
    ],
    features: [
      commonFeatures.originalWebsite,
      commonFeatures.privateConversation,
      { 
        icon: '🔌', 
        titleKey: 'productDetail.sora.features.modelLimit.title',
        descKey: 'productDetail.sora.features.modelLimit.desc'
      },
      commonFeatures.neverBanned
    ],
    faqKeys: [
      commonFAQ.freeTrial,
      commonFAQ.refund,
      {
        questionKey: 'productDetail.sora.faq.whatIsService.q',
        answerKey: 'productDetail.sora.faq.whatIsService.a'
      },
      {
        questionKey: 'productDetail.sora.faq.usageLimit.q',
        answerKey: 'productDetail.sora.faq.usageLimit.a'
      },
      commonFAQ.conversationPrivacy,
      commonFAQ.exclusiveAccount,
      commonFAQ.wantExclusive,
      {
        questionKey: 'productDetail.sora.faq.supportedFeatures.q',
        answerKey: 'productDetail.sora.faq.supportedFeatures.a'
      },
      {
        questionKey: 'productDetail.sora.faq.difference.q',
        answerKey: 'productDetail.sora.faq.difference.a'
      },
      commonFAQ.support
    ],
    versions: [
      {
        label: '1 month',
        month: 1,
        price: 6.9,
        originalPrice: 20,
        stock: 99,
        productId: '237',
        key: 'basic',
        durationKey: 'productDetail.common.duration.month'
      },
      {
        label: '3 months',
        month: 3,
        price: 17.7,
        originalPrice: 60,
        stock: 99,
        productId: '238',
        key: 'pro',
        durationKey: 'productDetail.common.duration.month'
      },
      {
        label: '12 months',
        month: 12,
        price: 58.8,
        originalPrice: 240,
        stock: 99,
        productId: '239',
        key: 'enterprise',
        durationKey: 'productDetail.common.duration.month'
      }
    ],
    specs: [
      { labelKey: 'productDetail.common.specs.environment', valueKeys: ['productDetail.common.specs.values.webBrowser'] },
      { labelKey: 'productDetail.common.specs.productType', valueKeys: ['productDetail.common.specs.values.sharedFleet'] },
      { labelKey: 'productDetail.common.specs.accountType', valueKeys: ['productDetail.common.specs.values.sharedFleetAccess'] },
      { labelKey: 'productDetail.common.specs.dataPrivacy', valueKeys: ['productDetail.common.specs.values.privateConversation'] },
      { labelKey: 'productDetail.common.specs.warranty', valueKeys: ['productDetail.common.specs.values.warrantyDuration'] },
      { labelKey: 'productDetail.common.specs.packageIncludes', values: ['GPT+Sora'] },
      { labelKey: 'productDetail.common.specs.loginEnvironment', valueKeys: ['productDetail.common.specs.values.browserAiMonkey'] },
      { labelKey: 'productDetail.common.specs.officialPrice', values: ['20$/月'] },
      { labelKey: 'productDetail.common.specs.rateLimit', valueKeys: ['productDetail.sora.specs.rateLimit'] }
    ]
  },

  // Grok Super 产品配置
  grok: {
    id: 'grok',
    nameKey: 'shop.categories.grok',
    type: 'web',
    descriptionKey: 'productDetail.grok.description',
    highlightKeys: [
      'productDetail.common.highlights.originalWebsite',
      'productDetail.common.highlights.freeTrial',
      'productDetail.common.highlights.internetSearch',
      'productDetail.common.highlights.deepThinking',
      'productDetail.common.highlights.fileRecognition'
    ],
    features: [
      commonFeatures.originalWebsite,
      commonFeatures.privateConversation,
      { 
        icon: '🔌', 
        titleKey: 'productDetail.grok.features.modelLimit.title',
        descKey: 'productDetail.grok.features.modelLimit.desc'
      },
      commonFeatures.neverBanned
    ],
    faqKeys: [
      commonFAQ.freeTrial,
      commonFAQ.refund,
      {
        questionKey: 'productDetail.grok.faq.whatIsService.q',
        answerKey: 'productDetail.grok.faq.whatIsService.a'
      },
      {
        questionKey: 'productDetail.grok.faq.usageLimit.q',
        answerKey: 'productDetail.grok.faq.usageLimit.a'
      },
      commonFAQ.conversationPrivacy,
      commonFAQ.exclusiveAccount,
      commonFAQ.wantExclusive,
      {
        questionKey: 'productDetail.grok.faq.supportedFeatures.q',
        answerKey: 'productDetail.grok.faq.supportedFeatures.a'
      },
      {
        questionKey: 'productDetail.grok.faq.difference.q',
        answerKey: 'productDetail.grok.faq.difference.a'
      },
      commonFAQ.support
    ],
    versions: [
      {
        label: '1 month',
        month: 1,
        price: 8.9,
        originalPrice: 60,
        stock: 99,
        productId: '234',
        key: 'monthly',
        durationKey: 'productDetail.common.duration.month'
      },
      {
        label: '3 months',
        month: 3,
        price: 23.7,
        originalPrice: 90,
        stock: 99,
        productId: '235',
        key: 'quarterly',
        durationKey: 'productDetail.common.duration.month'
      },
      {
        label: '12 months',
        month: 12,
        price: 82.8,
        originalPrice: 360,
        stock: 99,
        productId: '236',
        key: 'yearly',
        durationKey: 'productDetail.common.duration.month'
      }
    ],
    specs: [
      { labelKey: 'productDetail.common.specs.environment', valueKeys: ['productDetail.common.specs.values.webBrowser'] },
      { labelKey: 'productDetail.common.specs.productType', valueKeys: ['productDetail.common.specs.values.sharedFleet'] },
      { labelKey: 'productDetail.common.specs.accountType', valueKeys: ['productDetail.common.specs.values.sharedFleetAccess'] },
      { labelKey: 'productDetail.common.specs.dataPrivacy', valueKeys: ['productDetail.common.specs.values.privateConversation'] },
      { labelKey: 'productDetail.common.specs.warranty', valueKeys: ['productDetail.common.specs.values.warrantyDuration'] },
      { labelKey: 'productDetail.common.specs.packageIncludes', values: ['GPT+Sora+Claude+Grok'] },
      { labelKey: 'productDetail.common.specs.loginEnvironment', valueKeys: ['productDetail.common.specs.values.browserAiMonkey'] },
      { labelKey: 'productDetail.common.specs.officialPrice', values: ['20$/月'] },
      { labelKey: 'productDetail.common.specs.rateLimit', valueKeys: ['productDetail.grok.specs.rateLimit'] }
    ]
  },

  // ChatGPT APP 产品配置
  app: {
    id: 'app',
    nameKey: 'shop.categories.app',
    type: 'app',
    descriptionKey: 'productDetail.app.description',
    highlightKeys: [
      'productDetail.common.highlights.originalWebsite',
      'productDetail.common.highlights.freeTrial',
      'productDetail.gpt.highlights.o3Model',
      'productDetail.gpt.highlights.unlimited4o',
      'productDetail.common.highlights.voiceChat',
      'productDetail.common.highlights.internetSearch',
      'productDetail.common.highlights.fileRecognition'
    ],
    features: [
      commonFeatures.webAndApp,
      commonFeatures.changePassword,
      commonFeatures.banProtection
    ],
    faqKeys: [
      commonFAQ.refundApp,
      commonFAQ.freeTrialApp,
      commonFAQ.changePassword,
      commonFAQ.support
    ],
    versions: [
      {
        label: '1 month',
        month: 1,
        price: 17.9,
        originalPrice: 20,
        stock: 0,
        productId: '225',
        key: 'monthly',
        durationKey: 'productDetail.common.duration.month'
      }
    ],
    specs: [
      { labelKey: 'productDetail.common.specs.environment', valueKeys: ['productDetail.common.specs.values.webAndApp'] },
      { labelKey: 'productDetail.common.specs.productType', valueKeys: ['productDetail.common.specs.values.exclusiveAccount'] },
      { labelKey: 'productDetail.common.specs.accountType', valueKeys: ['productDetail.common.specs.values.exclusiveAccount'] },
      { labelKey: 'productDetail.common.specs.dataPrivacy', valueKeys: ['productDetail.common.specs.values.privateConversation'] },
      { labelKey: 'productDetail.common.specs.warranty', valueKeys: ['productDetail.common.specs.values.warrantyDuration'] },
      { labelKey: 'productDetail.common.specs.packageIncludes', values: ['GPT+Sora'] },
      { labelKey: 'productDetail.common.specs.loginEnvironment', valueKeys: ['productDetail.common.specs.values.openaiOfficial'] },
      { labelKey: 'productDetail.common.specs.officialPrice', values: ['20$/月'] },
      { labelKey: 'productDetail.common.specs.rateLimit', valueKeys: ['productDetail.common.specs.values.officialRateLimit'] }
    ]
  }
};

/**
 * 获取产品详情（国际化版本）
 * @param {string} type - 产品类型
 * @returns {object} 产品详情对象
 */
export const getProductDetailI18n = (type) => {
  const config = productDetailConfigI18n[type];
  if (!config) {
    console.warn(`未找到产品类型: ${type}，返回默认配置`);
    return null;
  }

  // 转换配置为实际的翻译文本
  return {
    id: config.id,
    name: t(config.nameKey),
    type: config.type,
    description: t(config.descriptionKey),
    highlights: config.highlightKeys.map(key => t(key)),
    features: config.features.map(feature => ({
      icon: feature.icon,
      title: t(feature.titleKey),
      desc: t(feature.descKey)
    })),
    faq: config.faqKeys.map(faq => ({
      question: t(faq.questionKey),
      answer: t(faq.answerKey, { product: t(config.nameKey) })
    })),
    versions: config.versions.map(version => ({
      ...version,
      duration: `${version.month}${t(version.durationKey)}`
    })),
    specs: config.specs.map(spec => ({
      label: t(spec.labelKey),
      values: spec.valueKeys 
        ? spec.valueKeys.map(key => t(key))
        : spec.values
    }))
  };
};

/**
 * 获取产品的原始配置（不进行翻译）
 * @param {string} type - 产品类型
 * @returns {object} 产品配置对象
 */
export const getProductConfigRaw = (type) => {
  return productDetailConfigI18n[type] || null;
};

/**
 * 获取产品的FAQ（国际化版本）
 * @param {string} type - 产品类型
 * @returns {array} FAQ数组
 */
export const getProductFAQ = (type) => {
  // 确保type是有效的字符串
  if (!type || typeof type !== 'string') {
    console.warn(`getProductFAQ: 无效的产品类型参数: ${type}`);
    return [];
  }
  
  const config = productDetailConfigI18n[type];
  if (!config || !config.faqKeys) {
    console.warn(`未找到产品类型 ${type} 的FAQ配置`);
    return [];
  }

  // 确保faqKeys是数组
  if (!Array.isArray(config.faqKeys)) {
    console.warn(`产品类型 ${type} 的faqKeys不是数组`);
    return [];
  }

  try {
    // 转换FAQ配置为实际的翻译文本
    return config.faqKeys.map(faq => ({
      question: t(faq.questionKey),
      answer: t(faq.answerKey, { product: t(config.nameKey) })
    }));
  } catch (error) {
    console.error(`处理产品类型 ${type} 的FAQ时出错:`, error);
    return [];
  }
}; 